import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiEnvelope } from "@/types/api";
import { env } from "@/lib/env/client";

// In dev, use relative URLs so Vite proxy handles routing (same-origin → cookies work).
// In production the app is served behind a reverse proxy on the same domain.
const API_BASE_URL = import.meta.env.DEV ? "" : env.VITE_API_URL;
const IS_DEV = import.meta.env.DEV;

// ---------------------------------------------------------------------------
// Axios Instance Configuration
// ---------------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    "Accept": "application/json",
    "Content-Type": "application/json",
  },
});

axiosInstance.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// CSRF Management
// ---------------------------------------------------------------------------

let csrfPromise: Promise<void> | null = null;

async function ensureCsrfCookie(): Promise<void> {
  if (typeof window === "undefined") return;

  // Axios automatically reads XSRF-TOKEN cookie, but we need to ensure it's set first
  // Usually by hitting /sanctum/csrf-cookie
  if (!document.cookie.includes("XSRF-TOKEN")) {
    if (!csrfPromise) {
      csrfPromise = axiosInstance.get("/sanctum/csrf-cookie").then(() => {
        csrfPromise = null;
      });
    }
    await csrfPromise;
  }
}

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

// Request Interceptor: Logging
axiosInstance.interceptors.request.use((config) => {
  if (IS_DEV) {
    console.groupCollapsed(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    if (config.data) console.log("Body:", config.data);
    console.groupEnd();
  }
  return config;
});

// Response Interceptor: Handling & Logging
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (IS_DEV) {
      console.groupCollapsed(`[API] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
      console.log("Response:", response.data);
      console.groupEnd();
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (IS_DEV) {
      console.groupCollapsed(`[API] ${error.response?.status || "ERR"} ${config?.method?.toUpperCase()} ${config?.url}`);
      console.log("Error:", error.response?.data || error.message);
      console.groupEnd();
    }

    // Handle 419 CSRF Token Mismatch - Retry once
    if (error.response?.status === 419 && config && !config._retry) {
      config._retry = true;
      await ensureCsrfCookie();
      return axiosInstance(config);
    }

    // Handle 401 Unauthorized — session expired
    if (error.response?.status === 401) {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.dispatchEvent(
          new CustomEvent("athr:unauthorized", {
            detail: (error.response?.data as any)?.error || "Session expired. Please log in again.",
          })
        );
      }
    }

    // Format error to match ApiEnvelope
    const errorPayload: ApiEnvelope<any> = {
      success: false,
      message: (error.response?.data as any)?.message || error.message || "An unexpected error occurred",
      code: error.response?.status,
    };

    // Return a resolved Promise with the error payload so the calling code handles it gracefully
    // instead of throwing (matching our previous fetch behavior)
    return { data: errorPayload };
  }
);

// ---------------------------------------------------------------------------
// Typed Request Wrapper
// ---------------------------------------------------------------------------

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

async function request<T>(
  method: string,
  path: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiEnvelope<T>> {
  // CSRF token is only required for state-mutating requests, not GETs
  if (MUTATING_METHODS.has(method.toLowerCase())) {
    await ensureCsrfCookie();
  }

  try {
    // For FormData, drop the instance-level "application/json" Content-Type so
    // axios/browser can set "multipart/form-data" with the correct boundary.
    const headers = data instanceof FormData
      ? { "Content-Type": undefined as unknown as string }
      : undefined;

    const response = await axiosInstance.request<ApiEnvelope<T>>({
      method,
      url: path,
      data,
      headers,
      ...config,
    });

    // If interceptor returned a formatted error payload (which is inside response.data because axios wraps it)
    // Wait, if interceptor returns 'response', then response.data is the payload.
    // If interceptor caught error and returned { data: errorPayload }, then we effectively have a 'successful' axios response structure.

    return response.data;
  } catch (error) {
    // Should be caught by interceptor, but just in case
    return {
      success: false,
      message: "Network Error or Unexpected Failure",
    };
  }
}

// ---------------------------------------------------------------------------
// Public API Client
// ---------------------------------------------------------------------------

export const apiClient = {
  /** Proactively fetch the CSRF cookie */
  initCsrf: () => ensureCsrfCookie(),

  /** GET request */
  get: <T>(path: string) => request<T>("GET", path),

  /** POST request */
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),

  /** PUT request */
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),

  /** PATCH request */
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),

  /** DELETE request */
  delete: <T>(path: string) => request<T>("DELETE", path),
};
